import type { Permission, Role, ViewKey } from '../access/role';
import type { LabGeofence } from '../access/geofence';
import type { LabSettings } from '../access/settings';
import type { Stage } from '../kit/stage';

export type Department = Stage | 'management' | 'reception';

export interface User {
  id: string; // "abdulrazaq"
  name: string;
  username: string;
  title: string; // "Ceramic technician"
  role: Role;
  department: Department;
  isIntakeOperator: boolean; // CAD/CAM staff who also do intake
  /**
   * A department supervisor: a technician who also approves Final Check.
   * Section 11 of the request of 20 September 2026.
   *
   * A FLAG, exactly as `isIntakeOperator` is, and not a role. A supervisor is a
   * ceramist who also does something more; making it a role would change their
   * home screen, their menu and their tab bar, none of which the client asked
   * for. The flag resolves into `finalcheck` on the session, through the same
   * `effectivePermissions` the per-person exceptions go through, so the matrix
   * still reads "Final Check: Management" and the exception sits on the person.
   */
  isSupervisor: boolean;
  disabled: boolean;
  /**
   * Where their photograph is served, or null or absent for none, when the
   * screens draw their initials. Request of 4 October 2026 (second), section 7.
   */
  avatarUrl?: string | null;
  createdAt: string;
  /** Exceptions to the role on this person's own record, a grant or a deny. Resolved by the server. */
  hasAccessOverrides: boolean;
}

/** A reduced person, for the assignment picker: id, name and department. */
export interface UserSummary {
  id: string;
  name: string;
  department: Department;
}

/**
 * One stage a person worked, on one case.
 *
 * A row per piece of work rather than per case: somebody who took a case back
 * after a rework worked it twice, and their record should say so.
 */
export interface UserCaseRow {
  kitId: string;
  jobOrderNo: string;
  /** Absent, not empty, for a caller without seeIdentity. Section 6 item 1. */
  dentistName?: string;
  patientName?: string;
  product: string;
  units: number;
  /** The bench this person worked. Usually not where the case is now. */
  stage: Stage;
  startedAt: string;
  completedAt: string | null;
  /** Active time, excluding idle. Null while the task is still running. */
  activeSeconds: number | null;
  rework: boolean;
  /** Where the case sits now. Null once it has left the building. */
  currentStage: Stage | null;
}

export interface Session {
  user: User;
  role: Role;
  permissions: Permission[]; // resolved for THIS user, do not recompute client side
  views: ViewKey[]; // screens this user may open
  homeView: ViewKey;
  serverTime: string; // used to correct client clock drift
  /** Resolved by the server. The check needs the role, so it rides here. */
  geofence: LabGeofence;
  /** The routing mode, resolved by the server, so the job view needs no second call. */
  settings: LabSettings;
  /**
   * True while this person is still on the password everybody was given. The
   * screen asks for a new one before anything else, and the server refuses
   * everything but changing it and signing out. Absent means false. Section 15
   * of the review of 2 October 2026.
   */
  mustChangePassword?: boolean;
}

/** Department labels, from the prototype's DEPTN. */
export const DEPARTMENT_LABEL: Record<Department, string> = {
  management: 'Management',
  reception: 'Reception & Logistics',
  intake: 'Intake',
  cadcam: 'CAD / CAM',
  plaster: 'Plaster',
  wax: 'Wax & Fitting',
  ceramic: 'Ceramic',
  finalcheck: 'Final Check',
  ready: 'Ready',
};

/**
 * Staff whose time is tracked, and who therefore appear on the productivity
 * screen. Reception, Accounts and Management do not work a bench.
 */
export function isProductionStaff(user: Pick<User, 'role'>): boolean {
  return user.role === 'tech' || user.role === 'cadcam';
}

/**
 * A user cannot revoke themselves. Section 7.10 item 8, returned as 400.
 * The rule is here so the admin screen disables the button using the same
 * function the server refuses on.
 */
export function canRevoke(actorUserId: string, targetUserId: string): boolean {
  return actorUserId !== targetUserId;
}
